import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { Observable } from 'rxjs';
import { ContentListResponseModel, ContentModel, ContentPermissionModel, FileContentModel, HtmlContentModel, SectionContentModel, BulkDeleteContentRequest, ExportContentItemModel } from '../../models/content.model';
import { BasePagedSortableRequest } from '../../models/models';
import { ImportRequest } from '../../models/models';

@Injectable({
  providedIn: 'root'
})
export class ContentService {

  jsonHeaders = new HttpHeaders().set('Content-Type', 'application/json');

  constructor(
    private http: HttpClient
  ) { }

  ngOnInit() { }

  ngOnDestroy(): void { }

  private api = `${environment.baseV5ApiUri}/content`;

  getContent(name: string, locale?: string) {
    const localeSuffix = (!locale || locale === 'en') ? '' : '_' + locale;
    return this.http.get<ContentModel>(`${this.api}/name/${name + localeSuffix}`);
  }

  getList(request: BasePagedSortableRequest) {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(request)) {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, value.toString());
      }
    }

    return this.http.get<ContentListResponseModel>(`${this.api}`, { params })
  }

  processContent(content: ContentModel, file: File, thumbnail: File = null): Observable<any> {
    let request: Observable<any>;
    switch (content.type) {
      case 'File':
        let fileContentModel = <FileContentModel>content;
        fileContentModel.file = file;
        fileContentModel.thumbnail = thumbnail;
        request = this.upsertFileContent(fileContentModel);
        break;
      case 'Section':
        let sectionContentModel = <SectionContentModel>content;
        request = !sectionContentModel.id
          ? this.createContent(sectionContentModel)
          : this.updateContent(sectionContentModel);
        break;
      case 'Video':
        let videoContentModel = <FileContentModel>content;
        videoContentModel.file = file;
        videoContentModel.thumbnail = thumbnail;
        request = this.upsertFileContent(videoContentModel);
        break;
      default: // Html Content
        let htmlContentModel = <HtmlContentModel>content;
        htmlContentModel.html = <string>htmlContentModel.value;
        request = !htmlContentModel.id
          ? this.createContent(htmlContentModel)
          : this.updateContent(htmlContentModel);
        break;
    }

    return request;
  }

  createContent(content: ContentModel) : Observable<string> {
    const formData = this.mapContentToFormData(content);

    return this.http.post<string>(
      this.api,
      formData
    );
  }

  updateContent(content: ContentModel) {
    const formData = this.mapContentToFormData(content);

    return this.http.put<ContentModel>(
      `${this.api}/${content.id}`,
      formData
    );
  }

  upsertFileContent(content: FileContentModel) {
    const formData = this.mapContentToFormData(content);

    if (content.file) {
      let mainFile = new Blob([content.file], { type: content.file.type });
      formData.append('main', mainFile, content.file.name);
    }

    if (content.thumbnail) {
      let thumbnailFile = new Blob([content.thumbnail], { type: content.thumbnail.type });
      formData.append('thumbnail', thumbnailFile, content.thumbnail.name);
    }
    return content.id
        ? this.http.put(`${this.api}/${content.id}`, formData) // update
        : this.http.post(this.api, formData); // insert
  }

  deleteContent(id: string) {
    return this.http.delete(`${this.api}/${id}`);
  }

  validatePermission(permission: ContentPermissionModel): Observable<{ isValid: boolean; message?: string | null }> {
    return this.http.post<{ isValid: boolean; message?: string | null }>(`${this.api}/validate-permission`, permission);
  }

  private mapContentToFormData(content: ContentModel): FormData {
    const requestModel : ContentModel = {
        id: content.id,
        type: content.type,
        createdBy: content.createdBy,
        name: content.name,
        isActive: content.isActive,
        description: content.description,
        value: content.value,
        order: content.order,
        sectionId: content.sectionId
    };

    const formData = new FormData();

    // Append the rest of the form data
    for (const [key, value] of Object.entries(requestModel)) {
        if (value !== undefined && value !== null && value !== '') {
            formData.append(key, value.toString());
        }
    }

    (content.permissions || []).forEach((permission, index) => {
      if (permission?.roleName) {
        formData.append(`permissions[${index}].roleName`, permission.roleName);
      }

      if (permission?.roleEntity) {
        formData.append(`permissions[${index}].roleEntity`, permission.roleEntity);
      }
    });

    return formData;
  }

  export(ids: string[]): Observable<{ contentItems: ExportContentItemModel[] }> {
    const request = { ids: ids };
    return this.http.post<{ contentItems: ExportContentItemModel[] }>(`${this.api}/export`, request);
  }

  validateImport(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('json', file);
    return this.http.post(`${this.api}/validate-import`, formData);
  }

  import(request: ImportRequest): Observable<string[]> {
    return this.http.post<string[]>(`${this.api}/import`, request);
  }

  bulkDelete(request: BulkDeleteContentRequest): Observable<any> {
    return this.http.delete(`${this.api}/bulk-delete`, { body: request });
  }
}
